import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Home, Map as MapIcon, MapPin, Car, ShoppingCart, UserCircle2, LogIn, X } from "lucide-react";
import { CHAD_REGIONS } from "../lib/chad";
import { cartTotals } from "../lib/grocery";
import { useEffect } from "react";

/**
 * Native-feel bottom tab bar — visible on mobile (<md) only.
 * Five tabs : Home · Map · Regions · Pricing · Account.
 * "Regions" opens a bottom sheet listing the 23 regions.
 * "Account" deeplinks to /annonceur, /admin or /login depending on the role.
 */
export default function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [regionsOpen, setRegionsOpen] = useState(false);
  const [cartCount, setCartCount] = useState(() => cartTotals().count);

  useEffect(() => {
    const refresh = () => setCartCount(cartTotals().count);
    window.addEventListener("kanama-cart-changed", refresh);
    return () => window.removeEventListener("kanama-cart-changed", refresh);
  }, []);

  const accountPath = (() => {
    if (!user || user === false) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "owner") return "/annonceur";
    return "/login";
  })();

  const accountLabel = (() => {
    if (!user || user === false) return t("bottomNav.signIn");
    return t("bottomNav.account");
  })();

  const tabs = [
    { to: "/", icon: Home, label: t("bottomNav.home"), testid: "bn-home", exact: true },
    { to: "/carte", icon: MapIcon, label: t("bottomNav.map"), testid: "bn-map" },
    { to: "/ride", icon: Car, label: t("bottomNav.ride"), testid: "bn-ride", highlight: true },
    { to: "/courses", icon: ShoppingCart, label: "Courses", testid: "bn-courses", badge: cartCount },
    {
      to: accountPath,
      icon: !user || user === false ? LogIn : UserCircle2,
      label: accountLabel,
      testid: "bn-account",
    },
  ];

  const isActive = (tab) => {
    if (tab.onClick) return regionsOpen;
    if (tab.exact) return location.pathname === tab.to;
    return location.pathname.startsWith(tab.to);
  };

  const handleRegionTap = (capital) => {
    setRegionsOpen(false);
    navigate(`/carte?city=${encodeURIComponent(capital)}`);
  };

  return (
    <>
      {/* Bottom Tab Bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-brand-line"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
        data-testid="bottom-nav"
        aria-label="Mobile primary navigation"
      >
        <ul className="flex items-stretch justify-around">
          {tabs.map((tab, i) => {
            const Icon = tab.icon;
            const active = isActive(tab);
            const baseCls =
              "relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 select-none touch-manipulation transition-colors active:bg-brand-sandlight/60";
            const tone = active ? "text-brand-terracotta" : (tab.highlight ? "text-brand-goldDark" : "text-brand-inkSoft hover:text-brand-ink");
            const indicator = (
              <span
                aria-hidden="true"
                className={`absolute top-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-brand-terracotta transition-all duration-200 ${
                  active ? "w-8 opacity-100" : "w-0 opacity-0"
                }`}
              />
            );
            const content = (
              <>
                {indicator}
                <div className="relative">
                  <Icon
                    className={`w-5 h-5 transition-transform duration-200 ${active ? "scale-110" : "scale-100"}`}
                    strokeWidth={active ? 2.4 : 1.8}
                  />
                  {tab.badge > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2.5 bg-brand-terracotta text-white text-[9px] font-bold rounded-full px-1 min-w-[16px] h-4 flex items-center justify-center leading-none"
                      data-testid={`${tab.testid}-badge`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium tracking-tight ${active ? "font-semibold" : ""}`}>
                  {tab.label}
                </span>
              </>
            );
            return (
              <li key={i} className="flex">
                {tab.onClick ? (
                  <button
                    type="button"
                    onClick={tab.onClick}
                    className={`${baseCls} ${tone}`}
                    data-testid={tab.testid}
                    aria-pressed={active}
                  >
                    {content}
                  </button>
                ) : (
                  <NavLink to={tab.to} end={tab.exact} className={`${baseCls} ${tone}`} data-testid={tab.testid}>
                    {content}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Regions bottom sheet */}
      {regionsOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex items-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="regions-sheet-title"
          data-testid="regions-sheet"
        >
          <button
            type="button"
            onClick={() => setRegionsOpen(false)}
            className="absolute inset-0 bg-black/45 animate-in fade-in duration-200"
            aria-label="Close"
            data-testid="regions-sheet-backdrop"
          />
          <div
            className="relative w-full max-h-[82vh] bg-white rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-300"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          >
            <div className="flex items-center justify-between px-5 pt-3 pb-2 border-b border-brand-line">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-brand-line" aria-hidden="true" />
              <h3 id="regions-sheet-title" className="font-heading text-base font-semibold text-brand-ink mt-2">
                {t("bottomNav.regions.sheetTitle")}
              </h3>
              <button
                type="button"
                onClick={() => setRegionsOpen(false)}
                className="mt-2 -mr-2 p-2 rounded-full text-brand-inkSoft hover:text-brand-ink active:bg-brand-sandlight"
                aria-label="Close"
                data-testid="regions-sheet-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-3 py-2 grid grid-cols-2 gap-1.5">
              {CHAD_REGIONS.map((r) => (
                <button
                  key={r.region}
                  type="button"
                  onClick={() => handleRegionTap(r.capital)}
                  className="flex items-start gap-2 py-2.5 px-3 rounded-xl text-left active:bg-brand-sandlight hover:bg-brand-sandlight/60 transition-colors touch-manipulation min-h-[48px]"
                  data-testid={`sheet-region-${r.region.replace(/[^a-zA-Z]/g, "-")}`}
                >
                  <MapPin className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-medium text-brand-ink">{r.region}</span>
                    <span className="text-[10px] uppercase tracking-wider text-brand-inkMuted">{r.capital}</span>
                  </div>
                </button>
              ))}
            </div>
            <Link
              to="/carte"
              onClick={() => setRegionsOpen(false)}
              className="block mx-3 mb-3 mt-1 text-center py-3 rounded-full bg-brand-ink text-white font-semibold active:bg-brand-inkSoft"
              data-testid="regions-sheet-view-all"
            >
              {t("bottomNav.regions.viewAll")}
            </Link>
          </div>
        </div>
      )}

      {/* Spacer so app content is not hidden behind the bottom tab bar */}
      <div
        className="md:hidden"
        style={{ height: "calc(64px + env(safe-area-inset-bottom, 0))" }}
        aria-hidden="true"
      />
    </>
  );
}
